import React, { useState, useEffect } from 'react';
import {
    Grid as KGrid,
    GridColumn as Column,
    GridColumnMenuCheckboxFilter,
    GridToolbar
} from "@progress/kendo-react-grid";
import { ImportButton } from "../import/importButton";
import { green, red } from "@material-ui/core/colors";
import { Button } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import SaveIcon from '@material-ui/icons/Save';
import CsvDownloader from 'react-csv-downloader';
import { useMutation } from 'react-admin';
import { useNotify } from 'react-admin';
import CircularProgress from '@material-ui/core/CircularProgress';

const AllocationSheet = ({ filteredPayrolls, costCenters, onUpdate, payrollRun }) => {
    const [editID, setEditID] = useState(null);
    const [sort, setSort] = useState([{ field: 'orgID', dir: 'asc' }]);
    const [filter, setFilter] = useState(undefined);
    // Add state to force re-render when allocations change
    const [allocationUpdateTrigger, setAllocationUpdateTrigger] = useState(0);
    // Add state for grid data
    const [gridData, setGridData] = useState([]);
    
    const [mutate, { loading }] = useMutation();
    const notify = useNotify();

    const saveAllocations = () => {
        if (!payrollRun || !payrollRun.id) {
            notify('No payroll run selected', 'warning');
            return;
        }

        // Transform the dictionary into array format
        const transformedAllocations = Object.entries(payrollRun.payrollRunCostCenterAllocations || {}).map(([employeeId, costCenterAllocations]) => ({
            employeeId: parseInt(employeeId),
            costCenterAllocations,
        }));

        mutate({
            type: 'update',
            resource: 'payrollRuns',
            payload: {
                id: payrollRun.id,
                data: {
                    payrollRunCostCenterAllocations: transformedAllocations
                }
            }
        },
        {
            onSuccess: () => {
                notify('Allocations saved successfully', 'success');
            },
            onFailure: (error) => notify('Unable to save allocations', 'warning'),
        });
    };

    // Recalculate the grid data when dependencies change
    useEffect(() => {
        const newData = filteredPayrolls.map(payroll => {
            const allocations = {};
            costCenters.forEach(cc => {
                // Get allocations from payrollRun instead of payroll
                const payrollAllocations = (payrollRun && 
                    payrollRun.payrollRunCostCenterAllocations && 
                    payrollRun.payrollRunCostCenterAllocations[payroll.employeeId]) || [];
                    
                const allocation = payrollAllocations.find(
                    a => a.costCenterCode === cc.code
                );
                allocations[`allocation_${cc.code}`] = allocation && allocation.allocationPercentage ? allocation.allocationPercentage : 0;
            });

            const total = Object.values(allocations).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)*100;

            return {
                ...payroll,
                ...allocations,
                total,
                inEdit: payroll.employeeId === editID
            };
        });
        
        setGridData(newData);
    }, [filteredPayrolls, costCenters, payrollRun, editID, allocationUpdateTrigger]);

    const handleItemChange = (event) => {
        const { dataItem, field, value } = event;
        const costCenterCode = field.replace('allocation_', '');
        
        // Find existing allocation or create new one
        let allocations = [];
        if (payrollRun && payrollRun.payrollRunCostCenterAllocations && payrollRun.payrollRunCostCenterAllocations[dataItem.employeeId]) {
            allocations = [...payrollRun.payrollRunCostCenterAllocations[dataItem.employeeId]];
        }
        
        const existingIndex = allocations.findIndex(a => a.costCenterCode === costCenterCode);
        
        if (existingIndex >= 0) {
            allocations[existingIndex] = {
                ...allocations[existingIndex],
                allocationPercentage: parseFloat(value) || 0
            };
        } else {
            const costCenter = costCenters.find(c => c.code === costCenterCode);
            allocations.push({
                costCenterCode,
                costCenterName: costCenter && costCenter.name ? costCenter.name : '',
                allocationPercentage: parseFloat(value) || 0
            });
        }

        onUpdate(dataItem.employeeId, allocations);
        // Trigger re-render after allocation update
        setAllocationUpdateTrigger(prev => prev + 1);
    };

    const prepareDownloadData = () => {
        return filteredPayrolls.map(payroll => {
            const row = {
                'orgID': payroll.orgID || '',
                'fullName': payroll.fullName || ''
            };

            // Get allocations from payrollRun
            const payrollAllocations = (payrollRun && 
                payrollRun.payrollRunCostCenterAllocations && 
                payrollRun.payrollRunCostCenterAllocations[payroll.employeeId]) || [];

            // Add allocation percentages for each cost center
            costCenters.forEach(cc => {
                const allocation = payrollAllocations.find(a => a.costCenterCode === cc.code);
                row[cc.code] = allocation ? allocation.allocationPercentage : 0;
            });

            return row;
        });
    };

    const ImportAllocationFromFile = ({ onImport }) => {
        const handleImport = (res, values) => {
            // console.log(values);
            const updates = values.map(row => {
                const allocations = [];
                Object.entries(row).forEach(([key, value]) => {
                    if (key !== 'orgID' && key !== 'fullName' && costCenters.find(c => c.code === key)) {
                        const costCenter = costCenters.find(c => c.code === key);
                        allocations.push({
                            costCenterCode: key,
                            costCenterName: costCenter && costCenter.name ? costCenter.name : '',
                            allocationPercentage: parseFloat(value) || 0
                        });
                    }
                });

                let employee = null;
                
                // First try to match by orgID if it exists
                if (row.orgID) {
                    employee = filteredPayrolls.find(p => {
                        const pOrgID = p.orgID || '';
                        const rowOrgID = row.orgID || '';
                        return pOrgID.replace(/\s+/g, '').toLowerCase() === rowOrgID.replace(/\s+/g, '').toLowerCase();
                    });
                }
                
                // If no match found by orgID and fullName exists in the row, try matching by fullName
                if (!employee && row.fullName) {
                    employee = filteredPayrolls.find(p => {
                        const pFullName = p.fullName || '';
                        const rowFullName = row.fullName || '';
                        return pFullName.replace(/\s+/g, '').toLowerCase() === rowFullName.replace(/\s+/g, '').toLowerCase();
                    });
                    console.log(employee);
                }

                return {
                    employeeId: employee ? employee.employeeId : null,
                    allocations
                };
            }).filter(update => update.employeeId);

            // console.log(updates);
            updates.forEach(update => {
                onUpdate(update.employeeId, update.allocations);
            });
            
            // Trigger re-render after import
            setAllocationUpdateTrigger(prev => prev + 1);
        };

        return (
            <ImportButton 
                label='Import Allocations from File' 
                onImport={handleImport}
            />
        );
    };

    const DownloadAllocations = () => {
        const columns = [
            {
                id: 'orgID',
                displayName: 'orgID'
            },
            {
                id: 'fullName',
                displayName: 'fullName'
            },
            ...costCenters.map(cc => ({
                id: cc.code,
                displayName: cc.code
            }))
        ];

        return (
            <CsvDownloader
                filename={`allocations_${new Date().toISOString().split('T')[0]}`}
                extension=".csv"
                separator=","
                bom={false}
                wrapColumnChar=""
                columns={columns}
                datas={prepareDownloadData()}
            >
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<GetAppIcon />}
                    style={{ marginLeft: '10px' }}
                >
                    Download Allocations
                </Button>
            </CsvDownloader>
        );
    };

    return (
        <div style={{
            width: '120vw',
            overflowX: 'auto',
            overflowY: 'hidden'
        }}>
            <GridToolbar>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                
                }}>
                    <div onClick={() => setEditID(null)}>
                        <ImportAllocationFromFile onImport={(res, values) => onUpdate(values)} />
                    </div>
                    <DownloadAllocations />

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={saveAllocations}
                        disabled={loading}
                        style={{ marginLeft: 'auto' }}
                    >
                        {loading ? 'Saving...' : 'Save Allocations'}
                    </Button>
                </div>
            </GridToolbar>
            <div style={{
                overflowX: 'auto',
                overflowY: 'hidden',
                maxWidth: '100%'
            }}>
                <KGrid
                    style={{
                        maxHeight: '70vh',
                        minWidth: costCenters.length > 8 ? `${costCenters.length * 120}px` : 'auto'
                    }}
                    data={gridData}
                    editField="inEdit"
                    onItemChange={handleItemChange}
                    onRowClick={event => setEditID(event.dataItem.employeeId)}
                    sortable
                    sort={sort}
                    onSortChange={(e) => setSort(e.sort)}
                    filter={filter}
                    onFilterChange={(e) => setFilter(e.filter)}
                >
                <Column 
                    field="orgID" 
                    title="Work ID" 
                    width={120} 
                    editable={false}
                />
                <Column 
                    field="fullName" 
                    title="Name" 
                    editable={false}
                    columnMenu={props => 
                        <GridColumnMenuCheckboxFilter 
                            data={gridData} 
                            expanded={true} 
                            {...props}
                        />
                    }
                />
                {costCenters.map(cc => (
                    <Column
                        key={cc.code}
                        field={`allocation_${cc.code}`}
                        title={cc.code}
                        editor="numeric"
                        format="{0:n2}"
                    />
                ))}
                <Column
                    field="total"
                    title="Total"
                    editable={false}
                    cell={props => (
                        <td style={{ 
                            color: Math.abs(props.dataItem.total - 100) < 0.01 ? green[500] : red[500]
                        }}>
                            {props.dataItem.total.toFixed(2)}%
                        </td>
                    )}
                />
            </KGrid>
            </div>
        </div>
    );
};

export default AllocationSheet; 